import React, {useState} from 'react';
import PropTypes, { object } from 'prop-types';
import { useFlexLayout } from 'react-table';
import ReactTableConstructor from './components/ReactTableConstructor';
import ReactTableCell from './components/ReactTableCell';
import ReactTableCellEditable from './components/ReactTableEditableCell';

const ReactTableBase = ({
  useDataQuery,
  setPageSize,
  setPageIndex,
  queryParams,
  tableConfig,
  columns,
  data,
  updateDraggableData,
  updateEditableData,
  getRowDataId,
  onRowClick,
  filters,
  dataLength = null,
  csvData,
  onHeaderClick,
  onHeaderIconClick
}) => {
  const {
    isEditable = false,
    isResizable = false,
    isSortable = false,
    withDragAndDrop = false,
    withPagination = false,
    withSearchEngine = false,
    withHover = false,
    searchPlaceholder = 'Search...',
    manualPageSize = [10, 25, 50, 100],
    striped = false,
    manualPagination = false,
    pageCount = 0,
    withCsvDownload = false,
  } = tableConfig;

  const [filterValue, setFilterValue] = useState(null);

  const tableOptions = {
    columns,
    data,
    updateDraggableData,
    updateEditableData,
    setFilterValue,
    getRowDataId,
    onRowClick,
    defaultColumn: {},
    isEditable,
    withDragAndDrop: withDragAndDrop || false,
    dataLength: data.length,
    autoResetPage: false,
    disableSortBy: !isSortable,
    manualSortBy: !isSortable,
    manualGlobalFilter: !withSearchEngine && !(!withPagination || manualPagination),
    manualPagination: !withPagination || manualPagination,
    initialState: {
      pageIndex: 0,
      pageSize: manualPageSize ? manualPageSize[0] : 10,
      globalFilter: withSearchEngine && filterValue ? filterValue : '',
    },
  };

  if (pageCount > 0) {
    tableOptions.pageCount = pageCount;
  }

  if (dataLength !== null) {
    tableOptions.dataLength = dataLength;
  }

  let tableOptionalHook = [];
  if (isResizable) tableOptionalHook = [useFlexLayout];
  if (withSearchEngine) {
    tableOptions.defaultColumn = {
      Cell: ReactTableCell,
    };
  }
  if (isEditable) {
    tableOptions.defaultColumn = {
      Cell: ReactTableCellEditable,
    };
  }

  return (
    <ReactTableConstructor
      key={isResizable || isEditable ? 'modified' : 'common'}
      tableConfig={tableConfig}
      tableOptions={tableOptions}
      tableOptionalHook={tableOptionalHook}
      getRowDataId={getRowDataId}
      onRowClick={onRowClick}
      onHeaderClick={onHeaderClick}
      onHeaderIconClick={onHeaderIconClick}
      filters={filters}
      useDataQuery={useDataQuery}
      queryParams={queryParams}
      setQueryPageSize={setPageSize}
      setQueryPageIndex={setPageIndex}
      csvData={csvData}
    />
  );
};

ReactTableBase.propTypes = {
  tableConfig: PropTypes.shape({
    isEditable: PropTypes.bool,
    isResizable: PropTypes.bool,
    isSortable: PropTypes.bool,
    withDragAndDrop: PropTypes.bool,
    withPagination: PropTypes.bool,
    withSearchEngine: PropTypes.bool,
    withHover: PropTypes.bool,
    searchPlaceholder: PropTypes.string,
    manualPageSize: PropTypes.arrayOf(PropTypes.number),
    withCsvDownload:PropTypes.bool
  }),
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
  })),
  data: PropTypes.arrayOf(PropTypes.shape()),
  updateDraggableData: PropTypes.func,
  updateEditableData: PropTypes.func,
  getRowDataId: PropTypes.func,
  onRowClick: PropTypes.func,
  filters:PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  dataLength: PropTypes.number,
  csvData:PropTypes.arrayOf(object),
  onHeaderClick: PropTypes.func,
  onHeaderIconClick: PropTypes.func,
}
ReactTableBase.defaultProps = {
  tableConfig: {},
  columns: [],
  data: [],
  setFilterName:null,
  setFilterState:null,
  updateDraggableData: () => {},
  updateEditableData: () => {}
};

export default ReactTableBase;
